import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Container,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

import DonationPI from '../public/DonationPI';
import DonationLineCorrections from './DonationLineCorrections';
import PageHeader from '../../../Components/PageHeader';
import {
  getDonationDetail,
  updateInternalDonationDonor,
} from '../../../endpoints/donations';
import { useApiGet } from '../../../utils/httpClient';
import { getMyPermissions } from '../../../endpoints/usersTs';
import { hasUserApplicationPermission } from '../../../utils/permissions';

const steps = ['Donor', 'Line copy'];

export default function InternalDonationEdit() {
  const { id: donationId } = useParams();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [donationObject, setDonationObject] = useState(null);

  const { data: permissions } = useApiGet(
    ['my-permissions-internal-donation-edit', donationId],
    () => getMyPermissions({ international: '1' }),
    { enabled: true }
  );

  const canManage = hasUserApplicationPermission(
    permissions,
    'donations:manage'
  );

  const { data: donationData, refetch } = useApiGet(
    ['internal-donation-edit', donationId],
    getDonationDetail,
    { enabled: !!donationId && canManage, refetchOnMount: 'always' },
    { id: donationId }
  );

  useEffect(() => {
    if (donationData) {
      setDonationObject(donationData);
    }
  }, [donationData]);

  useEffect(() => {
    if (permissions && !canManage) {
      navigate('/internal/donations', { replace: true });
    }
  }, [permissions, canManage, navigate]);

  const handleNext = useCallback(() => {
    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep((s) => Math.max(s - 1, 0));
  }, []);

  const saveDonor = useCallback(
    (data) => updateInternalDonationDonor({ id: donationId, ...data }),
    [donationId]
  );

  if (permissions && !canManage) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Button
        component={RouterLink}
        to={`/internal/donations/${donationId}`}
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2 }}
      >
        Back to donation
      </Button>
      <PageHeader title="Edit donation" />
      <Paper sx={{ p: 3, mt: 2 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && donationId && (
          <DonationPI
            handleNext={handleNext}
            setDonationId={() => {}}
            refetch={refetch}
            donationObject={donationObject}
            donationId={donationId}
            saveDonation={saveDonor}
            emailOptional
            correctionsMode
            allowMemberLink={false}
            allowOrganizationDonor
            addressOptional
          />
        )}

        {activeStep === 1 && donationId && (
          <DonationLineCorrections
            donationId={donationId}
            donationObject={donationObject}
            refetch={refetch}
            handleBack={handleBack}
            onSaved={() => navigate(`/internal/donations/${donationId}`)}
          />
        )}

        {activeStep === 0 && !donationObject && (
          <Typography color="text.secondary">Loading…</Typography>
        )}
      </Paper>
    </Container>
  );
}
